import type { Work } from '../types/api';
import { paragraphs } from './text';

export type WorkBlock = { kind: 'p' | 'h' | 'li'; text: string };
export type WorkFact = { label: string; value: string };

/** Lignes « Étiquette : valeur » du texte d'un projet qu'on déplace dans la fiche (les autres restent dans le texte). */
const FACT_LABELS: Record<string, string> = {
  'type de projet': 'Type de projet',
  "maître d'ouvrage": "Maître d'ouvrage",
  "maître d’ouvrage": "Maître d'ouvrage",
  'durée': 'Durée',
  'superficie': 'Superficie',
  'surface': 'Surface',
  'budget': 'Budget',
};
const DUPLICATES = /^(localisation|lieu|client|statut|état|etat)\s*:/i;

/** Sépare le texte d'un projet en paragraphes, intertitres (« Répartition des espaces : ») et lignes de fiche. */
export function parseWorkText(text = ''): { blocks: WorkBlock[]; facts: WorkFact[] } {
  const blocks: WorkBlock[] = [];
  const facts: WorkFact[] = [];
  for (const line of paragraphs(text)) {
    if (DUPLICATES.test(line)) continue;
    const m = line.match(/^([^:]{3,32})\s*:\s*(.{2,140})$/);
    const key = m?.[1].trim().toLowerCase();
    if (m && key && FACT_LABELS[key]) {
      facts.push({ label: FACT_LABELS[key], value: m[2].trim().replace(/\.$/, '') });
      continue;
    }
    const prev = blocks[blocks.length - 1];
    if (line.length <= 70 && /:$/.test(line)) blocks.push({ kind: 'h', text: line.replace(/\s*:$/, '') });
    // Intertitre court sans point final (« Description du projet ») ou ligne de liste sous un intertitre
    else if (prev && (prev.kind === 'h' || prev.kind === 'li') && line.length <= 90 && !/[.!?]$/.test(line)) blocks.push({ kind: 'li', text: line });
    else if (line.length <= 40 && !/[.!?:]$/.test(line) && !prev) blocks.push({ kind: 'h', text: line });
    else if (line.length <= 40 && !/[.!?:]$/.test(line) && prev && prev.kind === 'p') blocks.push({ kind: 'h', text: line });
    else blocks.push({ kind: 'p', text: line });
  }
  return { blocks, facts: facts.slice(0, 3) };
}

const MODES: Record<string, string> = { on_site: 'Sur site', remote: 'À distance', mixed: 'Mixte' };
export const deliveryLabel = (m = ''): string => MODES[m] ?? '';

const longDate = (iso?: string | null) => {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
};
export { longDate };

export type WorkStatus = { label: string; detail: string };

/** Les dates priment sur le drapeau « en cours » (la base marque « en cours » des projets déjà livrés). */
export function workStatus(w: Work): WorkStatus {
  const end = w.completed_date ? Date.parse(w.completed_date) : NaN;
  const hasEnd = !Number.isNaN(end);
  if (hasEnd && end <= Date.now()) return { label: 'Terminé', detail: `Livré le ${longDate(w.completed_date)}` };
  if (hasEnd) return { label: 'En cours', detail: `Livraison prévue le ${longDate(w.completed_date)}` };
  if (w.is_ongoing || w.start_date) return { label: 'En cours', detail: w.start_date ? `Démarré le ${longDate(w.start_date)}` : 'Chantier en cours' };
  return { label: 'À venir', detail: 'Chantier à démarrer' };
}
